import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useConnectQuickbooks } from "@hooks/useConnectQuickbooks";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { LeftArrowIcon } from "@icons";
import type { ExternalSystemChoice } from "@components/accountingMigration/source";
import {
    ExpandableSection,
    FullMigrationOptions,
    MigrateFromSelector,
    MigrationDateRange,
    getHistoricalDataSource,
    getSubView,
    useHistoricalDataToggles,
    useQbdJsonHistoricalDataDefaults,
    isMigrationDateRangeValid,
} from "@components/accountingMigration/historicalData";
import { ClientFormSchema } from "../../AddClientSteps/lib";
import containerStyles from "../../AddClientDrawerStepsContainer.module.scss";
import styles from "../AddClientDrawerSteps.module.scss";

interface Props {
    qboConnection?: IntegrationConnection | null;
    setQboConnection: (connection: IntegrationConnection | null) => void;
    onNext: () => void;
    onPrev: () => void;
    workspaceId?: string;
}

export const HistoricalDataStep: FC<Props> = ({
    qboConnection,
    setQboConnection,
    onNext,
    onPrev,
    workspaceId,
}) => {
    const { values, setFieldValue } = useFormikContext<ClientFormSchema>();
    const { historicalData, migrationMethod } = values;

    const [selectedSystem, setSelectedSystem] =
        useState<ExternalSystemChoice | null>(() =>
            migrationMethod ===
            AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE
                ? AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE
                : null,
        );

    const { connect: connectQBO, isPending: connectingQBO } =
        useConnectQuickbooks({
            workspaceId,
            onSuccess: (connection) => {
                setQboConnection(connection);
                setFieldValue(
                    "migrationMethod",
                    AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE,
                );
                setFieldValue("syncChartOfAccounts", true);
                if (!values.client.businessName) {
                    setFieldValue("client.businessName", connection.label);
                }
                onNext();
            },
        });

    const isQboConnected = !!qboConnection;
    const isNonQboMigration =
        !!migrationMethod &&
        migrationMethod !== AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE;
    const isQbdJsonMigration =
        migrationMethod ===
        AccountingMigrationExternalSystem.QUICKBOOKS_DESKTOP;
    const shouldShowPicker = !isQboConnected && !isNonQboMigration;

    const subView = useMemo(
        () =>
            getSubView(
                getHistoricalDataSource({
                    isQboConnected,
                    isQbdJsonMigration,
                    isNonQboMigration,
                    selectedSystem,
                }),
            ),
        [isQboConnected, isNonQboMigration, isQbdJsonMigration, selectedSystem],
    );

    useEffect(() => {
        if (subView === "glOnly") {
            if (!historicalData.syncGeneralLedger) {
                setFieldValue("historicalData.syncGeneralLedger", true);
            }
            if (historicalData.syncCounterparties) {
                setFieldValue("historicalData.syncCounterparties", false);
            }
            if (historicalData.syncAttachments) {
                setFieldValue("historicalData.syncAttachments", false);
            }
            if (historicalData.syncClasses) {
                setFieldValue("historicalData.syncClasses", false);
            }
            if (historicalData.syncCatalogItems) {
                setFieldValue("historicalData.syncCatalogItems", false);
            }
            if (historicalData.syncInvoices) {
                setFieldValue("historicalData.syncInvoices", false);
            }
            if (historicalData.syncBills) {
                setFieldValue("historicalData.syncBills", false);
            }
        }
    }, [
        subView,
        historicalData.syncGeneralLedger,
        historicalData.syncCounterparties,
        historicalData.syncAttachments,
        historicalData.syncClasses,
        historicalData.syncCatalogItems,
        historicalData.syncInvoices,
        historicalData.syncBills,
        setFieldValue,
    ]);

    useQbdJsonHistoricalDataDefaults({
        isQbdJsonMigration,
        historicalData,
        setFieldValue,
    });

    const handleSelectSystem = useCallback(
        (value: ExternalSystemChoice) => {
            if (connectingQBO) {
                return;
            }
            setSelectedSystem(value);
        },
        [connectingQBO],
    );

    const toggles = useHistoricalDataToggles({
        historicalData,
        setFieldValue,
    });

    const handleStartDateChange = useCallback(
        (value: string | undefined) => {
            setFieldValue("historicalData.startDate", value);
        },
        [setFieldValue],
    );

    const handleEndDateChange = useCallback(
        (value: string) => {
            setFieldValue("historicalData.endDate", value);
        },
        [setFieldValue],
    );

    const areDatesValid = isMigrationDateRangeValid({
        startDate: historicalData.startDate,
        endDate: historicalData.endDate,
    });

    const isPickerWithQuickbooks =
        historicalData.migrate && shouldShowPicker && subView === "full";
    const isPickerWithUpload =
        historicalData.migrate && shouldShowPicker && subView === "glOnly";

    const isNextDisabled = useMemo(() => {
        if (connectingQBO) {
            return true;
        }
        if (!historicalData.migrate) {
            return false;
        }
        if (subView === "picker") {
            return true;
        }
        if (subView === "full" || subView === "glOnly") {
            return (
                !areDatesValid ||
                (!historicalData.syncGeneralLedger &&
                    !historicalData.syncCounterparties &&
                    !historicalData.syncClasses &&
                    !historicalData.syncCatalogItems &&
                    !historicalData.syncInvoices &&
                    !historicalData.syncBills)
            );
        }
        return false;
    }, [
        connectingQBO,
        historicalData.migrate,
        subView,
        areDatesValid,
        historicalData.syncGeneralLedger,
        historicalData.syncCounterparties,
        historicalData.syncClasses,
        historicalData.syncCatalogItems,
        historicalData.syncInvoices,
        historicalData.syncBills,
    ]);

    const handleNext = useCallback(() => {
        if (isNextDisabled) {
            return;
        }
        if (isPickerWithQuickbooks) {
            connectQBO();
            return;
        }
        if (isPickerWithUpload) {
            setFieldValue("migrationMethod", null);
        }
        onNext();
    }, [
        isNextDisabled,
        isPickerWithQuickbooks,
        isPickerWithUpload,
        connectQBO,
        setFieldValue,
        onNext,
    ]);

    const shouldShowDateRange = subView === "full" || subView === "glOnly";

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                Would you like to migrate historical data?
            </div>
            <div className={styles.subheader}>
                After onboarding, we'll walk you through the upload.
            </div>
            <div className={styles.migrationOptions}>
                <RadioGroup
                    value={historicalData.migrate ? "yes" : "no"}
                    onValueChange={(value) => {
                        const isMigrating = value === "yes";
                        setFieldValue("historicalData.migrate", isMigrating);
                        if (!isMigrating) {
                            setSelectedSystem(null);
                        }
                    }}
                    className={styles.optionsContainer}
                >
                    <Radio
                        value="no"
                        className={styles.option}
                        label="No, don't migrate"
                    />
                    <Radio
                        value="yes"
                        className={styles.option}
                        label="Yes, migrate historical data"
                    />
                </RadioGroup>

                <ExpandableSection isOpen={historicalData.migrate}>
                    <div className={styles.migrationDetailsBlock}>
                        {shouldShowPicker && (
                            <MigrateFromSelector
                                value={selectedSystem}
                                onChange={handleSelectSystem}
                                disabled={connectingQBO}
                            />
                        )}
                        <ExpandableSection isOpen={shouldShowDateRange}>
                            <div
                                className={classNames({
                                    [styles.migrationRevealedBlock]:
                                        shouldShowPicker,
                                })}
                            >
                                <ExpandableSection isOpen={subView === "full"}>
                                    <FullMigrationOptions
                                        syncGeneralLedger={
                                            historicalData.syncGeneralLedger
                                        }
                                        syncCounterparties={
                                            historicalData.syncCounterparties
                                        }
                                        syncAttachments={
                                            historicalData.syncAttachments
                                        }
                                        syncClasses={historicalData.syncClasses}
                                        syncCatalogItems={
                                            historicalData.syncCatalogItems
                                        }
                                        syncInvoices={
                                            historicalData.syncInvoices
                                        }
                                        syncBills={historicalData.syncBills}
                                        onToggleGeneralLedger={
                                            toggles.onToggleGeneralLedger
                                        }
                                        onToggleCounterparties={
                                            toggles.onToggleCounterparties
                                        }
                                        onToggleAttachments={
                                            toggles.onToggleAttachments
                                        }
                                        onToggleClasses={
                                            toggles.onToggleClasses
                                        }
                                        onToggleCatalogItems={
                                            toggles.onToggleCatalogItems
                                        }
                                        onToggleInvoices={
                                            toggles.onToggleInvoices
                                        }
                                        onToggleBills={toggles.onToggleBills}
                                        hideAttachments={isQbdJsonMigration}
                                        hideCounterparties={isQbdJsonMigration}
                                    />
                                </ExpandableSection>
                                <MigrationDateRange
                                    startDate={historicalData.startDate}
                                    endDate={historicalData.endDate}
                                    onStartDateChange={handleStartDateChange}
                                    onEndDateChange={handleEndDateChange}
                                />
                            </div>
                        </ExpandableSection>
                    </div>
                </ExpandableSection>
            </div>

            <div className={containerStyles.footer}>
                <Button
                    variant="primary"
                    type="button"
                    onClick={onPrev}
                    disabled={connectingQBO}
                    iconOnly
                    size="xl"
                    aria-label="Go back"
                >
                    <LeftArrowIcon />
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    type="button"
                    size="xl"
                    onClick={handleNext}
                    disabled={isNextDisabled}
                    loading={connectingQBO}
                    hotKey="mod+enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: "Next",
                        enabled: !isNextDisabled,
                        preventDefault: true,
                        enableOnFormTags: true,
                    }}
                >
                    Next
                </ButtonWithLoader>
            </div>
        </div>
    );
};
